import camelCase from 'lodash/fp/camelCase';
import castArray from 'lodash/fp/castArray';
import filter from 'lodash/fp/filter';
import flow from 'lodash/fp/flow';
import includes from 'lodash/fp/includes';
import isNil from 'lodash/fp/isNil';
import mapKeys from 'lodash/fp/mapKeys';
import omitBy from 'lodash/fp/omitBy';
import pickBy from 'lodash/fp/pickBy';
import upperCase from 'lodash/fp/upperCase';

import { paramsParser } from '@lumapps/base-api';
import { PostType } from '@lumapps/posts/types';
import type { GetWidgetURLFiltersByTypeOptions } from '@lumapps/widget-base/hooks/useWidgetURLFilters';

import { URLPostListFilterEnum, PostListFilterType, HasRelevantComment, PinnedPosts } from '../../types';

export interface UrlPostListFiltersOptionType extends GetWidgetURLFiltersByTypeOptions {
    availablePostTypes?: PostType[];
}

/**
 * Get the filters from the URL, filters by existing filters and clean the values when its possible
 * The expected URL format is the following: ?filters[filter1]=value1&filters[filter1]=value2&filters[filter2]=value3
 * It can optionally try to interpret v1 url filter format if the option is enabled
 * @returns object containing filters and their values
 */
export const getPostListURLFilters = (
    search: string,
    options: UrlPostListFiltersOptionType = {
        availablePostTypes: [],
    },
): Partial<PostListFilterType> => {
    const { isLegacyCompatible = false, availablePostTypes } = options;

    // It is possible to enable legacy compatibility by getting data from Url v1
    if (/^filters=query_/.test(search.slice(1)) && isLegacyCompatible) {
        return { searchQuery: search.slice(1).replace(/^filters=query_/, '') };
    }

    const { filters: rawFilters } = paramsParser(search, { ignoreQueryPrefix: true });

    if (!rawFilters) {
        return {};
    }

    const { authorEmail, hasRelevantComment, postCategories, postStatuses, postTypes, searchQuery, pinnedOnly } = flow(
        pickBy((value, key) => key in URLPostListFilterEnum),
        mapKeys(camelCase),
    )(rawFilters);

    return omitBy(isNil, {
        authorEmail: authorEmail
            ? [
                  {
                      email: authorEmail as string,
                      firstName: undefined,
                      lastName: undefined,
                  },
              ]
            : undefined,
        hasRelevantComment:
            hasRelevantComment in HasRelevantComment ? castArray(hasRelevantComment as HasRelevantComment) : undefined,
        postCategories: postCategories ? (castArray(postCategories) as string[]) : undefined,
        postStatuses: postStatuses ? (castArray(postStatuses) as string[]) : undefined,
        postTypes:
            postTypes && availablePostTypes
                ? (flow(
                      castArray,
                      filter((type) => includes(upperCase(type as string), availablePostTypes)),
                  )(postTypes) as PostType[])
                : undefined,
        searchQuery: searchQuery as string | undefined,
        pinnedOnly: pinnedOnly in PinnedPosts ? castArray(pinnedOnly as PinnedPosts) : undefined,
    });
};
